import { useState } from "react";
import ConfirmDialog from "components/admin/ConfirmDialog";
import { BUTTON } from "components/admin/DetailPanel";
import { apiPost } from "lib/apiClient";
import { formatKes } from "lib/format";
import { actionUrl, availableActions, describeActionError } from "./marketActions";

const INPUT =
  "mt-1 w-full rounded-md border border-border bg-transparent p-2.5 text-sm text-foreground outline-none focus:border-ring focus:ring-[3px] focus:ring-ring/50 dark:bg-input/30 placeholder:text-muted-foreground";

function summarizeMoneyResult(key, res) {
  if (!res) return null;
  if (key === "settle-positions") {
    return `${res.positionsSettled} position(s) paid, ${formatKes(res.totalPaidOut)} paid out, ${formatKes(res.totalCommissionCollected)} commission collected.`;
  }
  if (key === "void-positions") {
    return `${res.positionsVoided} position(s) refunded, ${res.ordersCancelled} order(s) cancelled, ${formatKes(res.totalRefunded)} refunded.`;
  }
  return null;
}

export default function MarketActionPanel({ market, audience, decision, full = false, onChanged, emptyMessage }) {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [proposing, setProposing] = useState(false);

  const actions = availableActions(market, audience, { decision, full });

  async function run(reason) {
    setBusy(true);
    setError(null);
    try {
      const body = pending.noReason ? {} : { reason };
      const res = await apiPost(actionUrl(pending, market, audience), body);
      setNotice(summarizeMoneyResult(pending.key, res) || `${pending.label}: done.`);
      setPending(null);
      if (onChanged) onChanged(res?.market || null);
    } catch (err) {
      setError(describeActionError(err));
    } finally {
      setBusy(false);
    }
  }

  if (audience.loading) return null;

  if (actions.length === 0) {
    if (!emptyMessage) return null;
    return <p className="text-xs text-gray-500 dark:text-gray-400">{emptyMessage}</p>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            disabled={Boolean(action.disabledReason) || busy}
            title={action.disabledReason || undefined}
            className={BUTTON[action.button] || BUTTON.outline}
            onClick={() => {
              setError(null);
              setNotice(null);
              if (action.form === "propose") {
                setPending(null);
                setProposing(true);
              } else {
                setProposing(false);
                setPending(action);
              }
            }}
          >
            {action.label}
          </button>
        ))}
      </div>

      {actions
        .filter((a) => a.disabledReason)
        .map((a) => (
          <p key={`${a.key}-why`} className="mt-2 text-xs text-amber-600 dark:text-amber-300">
            {a.label}: {a.disabledReason}
          </p>
        ))}

      {notice && <p className="mt-3 rounded-xl bg-green-50 p-3 text-sm text-green-700 dark:bg-green-400/10 dark:text-green-300">{notice}</p>}
      {error && !pending && <p className="mt-3 rounded-xl bg-red-50 p-3 text-sm text-red-600 dark:bg-red-400/10 dark:text-red-300">{error}</p>}

      {proposing && (
        <ProposeResultForm
          market={market}
          audience={audience}
          onCancel={() => setProposing(false)}
          onProposed={(updated) => {
            setProposing(false);
            setNotice("Result proposed. A teammate other than you now confirms or rejects it.");
            if (onChanged) onChanged(updated);
          }}
        />
      )}

      <ConfirmDialog
        key={pending?.key || "none"}
        open={!!pending}
        title={pending?.title || pending?.label}
        description={pending?.description || "This is a market lifecycle action. It is recorded with the reason you give."}
        confirmLabel={pending?.label || "Confirm"}
        tone={pending?.money || pending?.button === "danger" ? "danger" : pending?.button === "success" ? "success" : "primary"}
        requireReason={!pending?.noReason}
        busy={busy}
        error={error}
        onConfirm={run}
        onCancel={() => {
          setPending(null);
          setError(null);
        }}
      />
    </div>
  );
}

function ProposeResultForm({ market, audience, onCancel, onProposed }) {
  const outcomes = market.outcomes || [];
  const [outcomeId, setOutcomeId] = useState(outcomes[0]?.id || "");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost(`/admin/tenants/${audience.tenantId}/markets/${market.id}/propose-result`, {
        proposedOutcomeId: outcomeId,
        evidence: evidence.trim() || null,
      });
      onProposed(res?.market || null);
    } catch (err) {
      setError(describeActionError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-xl border-2 border-brand-500/30 bg-white p-5 dark:bg-navy-800">
      <p className="text-base font-semibold text-navy-700 dark:text-white">Propose a result</p>
      <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
        Pick the outcome that happened and say how you know. A teammate other than you confirms it before anyone is paid.
      </p>
      <label className="mt-4 block text-sm font-medium text-navy-700 dark:text-white">
        Correct outcome
        <select className={INPUT} value={outcomeId} onChange={(e) => setOutcomeId(e.target.value)}>
          {outcomes.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label className="mt-3 block text-sm font-medium text-navy-700 dark:text-white">
        Evidence
        <textarea
          className={INPUT}
          rows={4}
          maxLength={2000}
          value={evidence}
          onChange={(e) => setEvidence(e.target.value)}
          placeholder="The source, a link, the measured value, and when it was published."
        />
      </label>
      {error && <p className="mt-3 text-sm font-medium text-red-500">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="rounded-sm border border-gray-200 px-4 py-2 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white">
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !outcomeId || !evidence.trim()}
          className="rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
        >
          {busy ? "Proposing…" : "Propose result"}
        </button>
      </div>
    </div>
  );
}
